/**
 * ReferralPage - Referral/Affiliate system page
 *
 * Features:
 * - Display unique referral code and link
 * - Copy to clipboard functionality
 * - Share buttons for social media
 * - Referral statistics
 * - Referral history
 */

import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import apiService, { ReferralCode, ReferralStats, Referral } from '../../services/api';
import AppLayout from '../shared/AppLayout';
import { useDesktopView } from '../../hooks/useDesktopView';
import DesktopLayout from '../vault/desktop/DesktopLayout';

// Loading fallback
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Stat Card Component
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  highlight?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, highlight }) => (
  <div className={`bg-gray-800 rounded-xl p-4 ${highlight ? 'border border-green-500/30' : ''}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`${highlight ? 'text-green-400' : 'text-gray-400'}`}>{icon}</div>
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${highlight ? 'text-green-400' : 'text-white'}`}>{value}</p>
  </div>
);

// Referral Row Component
interface ReferralRowProps {
  referral: Referral;
}

const ReferralRow: React.FC<ReferralRowProps> = ({ referral }) => {
  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    active: 'bg-green-500/20 text-green-400',
    churned: 'bg-red-500/20 text-red-400',
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-700 last:border-0">
      <div className="flex-1">
        <p className="text-white font-mono text-sm">{referral.referee_email}</p>
        <p className="text-gray-500 text-xs">{formatDate(referral.created_at)}</p>
      </div>
      <div className="flex items-center gap-4">
        {referral.converted_at && (
          <span className="text-gray-500 text-xs">
            Subscribed {formatDate(referral.converted_at)}
          </span>
        )}
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[referral.status]}`}>
          {referral.status_display}
        </span>
      </div>
    </div>
  );
};

// Main Referral Content Component
const ReferralContent: React.FC<{ isDesktop?: boolean }> = ({ isDesktop = false }) => {
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [error, setError] = useState<string>('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [codeRes, statsRes] = await Promise.all([
        apiService.referral.getCode(),
        apiService.referral.getStats(),
      ]);
      setReferralCode(codeRes);
      setStats(statsRes);
      setError('');
    } catch (err) {
      console.error('Error fetching referral data:', err);
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopy = async (type: 'code' | 'link') => {
    if (!referralCode) return;

    const text = type === 'code' ? referralCode.code : referralCode.referral_link;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (platform: 'twitter' | 'telegram' | 'whatsapp') => {
    if (!referralCode) return;

    const message = encodeURIComponent(
      `Join Grid Vault - the automated liquidity bot for Solana! Use my referral link: ${referralCode.referral_link}`
    );

    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${message}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(referralCode.referral_link)}&text=${message}`,
      whatsapp: `https://wa.me/?text=${message}`,
    };

    window.open(urls[platform], '_blank', 'noopener,noreferrer');
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure you want to generate a new referral code? Your old code will stop working.')) {
      return;
    }

    try {
      setRegenerating(true);
      const newCode = await apiService.referral.regenerateCode();
      setReferralCode(newCode);
    } catch (err) {
      console.error('Error regenerating code:', err);
      setError('Failed to regenerate code');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return <LoadingFallback />;
  }

  const content = (
    <div className={isDesktop ? 'max-w-4xl' : ''}>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className={`${isDesktop ? 'text-3xl' : 'text-2xl'} font-bold text-white mb-2`}>
          Referral Program
        </h1>
        <p className="text-gray-400">
          Invite friends and earn free subscription months when they subscribe
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6"
        >
          <p className="text-red-400">{error}</p>
        </motion.div>
      )}

      {/* Referral Code Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-2xl p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Your Referral Code</h2>

        {referralCode && (
          <>
            {/* Code Display */}
            <div className="bg-black rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <code className="text-2xl font-bold text-green-400 font-mono tracking-wider">
                  {referralCode.code}
                </code>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleCopy('code')}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg
                           flex items-center gap-2 transition-colors"
                >
                  {copied === 'code' ? (
                    <>
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Referral Link */}
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Referral Link</label>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  value={referralCode.referral_link}
                  readOnly
                  className="flex-1 px-4 py-3 bg-black border border-gray-700 text-gray-300 rounded-xl
                           text-sm font-mono overflow-hidden text-ellipsis"
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleCopy('link')}
                  className="px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl
                           flex items-center gap-2 transition-colors"
                >
                  {copied === 'link' ? (
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-wrap gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleShare('twitter')}
                className="px-4 py-2 bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 text-[#1DA1F2] rounded-lg
                         flex items-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Twitter
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleShare('telegram')}
                className="px-4 py-2 bg-[#0088cc]/20 hover:bg-[#0088cc]/30 text-[#0088cc] rounded-lg
                         flex items-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                Telegram
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleShare('whatsapp')}
                className="px-4 py-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] rounded-lg
                         flex items-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </motion.button>
            </div>

            {/* Regenerate Button */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="text-gray-500 hover:text-gray-300 text-sm flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {regenerating ? 'Regenerating...' : 'Generate new code'}
              </button>
            </div>
          </>
        )}
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`grid ${isDesktop ? 'grid-cols-4' : 'grid-cols-2'} gap-4 mb-6`}
        >
          <StatCard
            label="Total Invited"
            value={stats.total_invited}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
          <StatCard
            label="Active Subscribers"
            value={stats.active_subscribers}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Months Earned"
            value={stats.months_earned}
            highlight
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </motion.div>
      )}

      {/* How it works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gray-900 rounded-2xl p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-white mb-4">How it Works</h2>
        <div className={`grid ${isDesktop ? 'grid-cols-3' : 'grid-cols-1'} gap-6`}>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">1</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Share Your Link</h3>
              <p className="text-gray-400 text-sm">Send your referral link to friends interested in automated trading</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">2</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">They Subscribe</h3>
              <p className="text-gray-400 text-sm">When your friend signs up and subscribes to a paid plan</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">3</span>
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Earn Rewards</h3>
              <p className="text-gray-400 text-sm">You earn 1 free month of subscription for each referral</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Referral History */}
      {stats && stats.referrals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-900 rounded-2xl p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4">Referral History</h2>
          <div className="divide-y divide-gray-700">
            {stats.referrals.map((referral) => (
              <ReferralRow key={referral.id} referral={referral} />
            ))}
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {stats && stats.referrals.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-900 rounded-2xl p-8 text-center"
        >
          <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-white font-semibold mb-2">No Referrals Yet</h3>
          <p className="text-gray-400">
            Share your referral link with friends to start earning free months!
          </p>
        </motion.div>
      )}
    </div>
  );

  if (isDesktop) {
    return <DesktopLayout>{content}</DesktopLayout>;
  }

  return <AppLayout title="Referrals">{content}</AppLayout>;
};

// Main ReferralPage component with responsive switching
const ReferralPage: React.FC = () => {
  const { isDesktop } = useDesktopView();

  return <ReferralContent isDesktop={isDesktop} />;
};

export default ReferralPage;
